import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cnInstant, type FollowUpDTO, type ReportItemDTO, type ReportPageDTO } from '@wd/shared';
import { errorText } from '../../lib/api';
import { serverNow } from '../../lib/clock';
import { useReportActions } from '../../lib/queries';

export type SaveState = 'idle' | 'saving' | 'saved' | 'offline' | 'error';

/** 日报编辑的共享逻辑：本地草稿 + 防抖自动保存、追问回复、提交 / 撤回。桌面和手机共用。 */
export function useReportEditor(report: ReportPageDTO | undefined) {
  const actions = useReportActions();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const noteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;

  const editable = report?.status === 'draft';

  useEffect(() => {
    const onOnline = () => setSaveState((s) => (s === 'offline' ? 'saving' : s));
    const onOffline = () => setSaveState('offline');
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const save = useCallback(
    async (id: string): Promise<boolean> => {
      const text = draftsRef.current[id];
      if (text === undefined) return true;
      setSaveState(navigator.onLine ? 'saving' : 'offline');
      try {
        await actions.updateItem.mutateAsync({ id, text });
        setDrafts((d) => {
          if (d[id] !== text) return d; // 保存期间又改了，保留新的草稿
          const { [id]: _, ...rest } = d;
          return rest;
        });
        setSaveState('saved');
        setError(null);
        return true;
      } catch (e) {
        setSaveState(navigator.onLine ? 'error' : 'offline');
        setError(errorText(e));
        return false;
      }
    },
    [actions.updateItem],
  );

  const setItemText = useCallback(
    (id: string, text: string) => {
      setDrafts((d) => ({ ...d, [id]: text }));
      const old = timers.current.get(id);
      if (old) clearTimeout(old);
      timers.current.set(
        id,
        setTimeout(() => {
          timers.current.delete(id);
          void save(id);
        }, 700),
      );
    },
    [save],
  );

  /** 立即保存（失焦、⌘↵、提交前）；返回是否全部保存成功 */
  const flush = useCallback(async () => {
    const pending = [...timers.current.keys()];
    for (const id of pending) {
      clearTimeout(timers.current.get(id));
      timers.current.delete(id);
    }
    const results = await Promise.all(Object.keys(draftsRef.current).map((id) => save(id)));
    return results.every(Boolean);
  }, [save]);

  const revert = useCallback((id: string) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
    setDrafts((d) => {
      const { [id]: _, ...rest } = d;
      return rest;
    });
  }, []);

  const textOf = useCallback((item: ReportItemDTO) => drafts[item.id] ?? item.text, [drafts]);

  // ---------- 追问 ----------
  const pendingManager = useMemo(() => report?.managerFollowUps.filter((f) => !f.answer) ?? [], [report]);
  const pendingAi = useMemo(() => report?.aiFollowUps.filter((f) => !f.answer && !f.skipped) ?? [], [report]);
  /** 回复框当前针对的追问：主管的优先 */
  const replyTarget: FollowUpDTO | null = editable ? (pendingManager[0] ?? pendingAi[0] ?? null) : null;

  const answer = useCallback(
    async (f: FollowUpDTO, text: string): Promise<boolean> => {
      const t = text.trim();
      if (!t) return true;
      setError(null);
      try {
        await actions.answer.mutateAsync({ id: f.id, answer: t });
        setReply('');
        setNotice(f.kind === 'manager' ? `已回复${f.fromName}，回复写进了 ${f.projectCode ?? ''} 今天的进展` : `已写入 ${f.projectCode ?? ''} 今天的进展`);
        return true;
      } catch (e) {
        setError(errorText(e));
        return false;
      }
    },
    [actions.answer],
  );

  const skip = useCallback(
    async (f: FollowUpDTO) => {
      try {
        await actions.skip.mutateAsync(f.id);
      } catch (e) {
        setError(errorText(e));
      }
    },
    [actions.skip],
  );

  // ---------- 补充说明：没有追问时，底部输入框就是补充说明（自动保存） ----------
  const note = noteDraft ?? report?.note ?? '';
  const noteDraftRef = useRef(noteDraft);
  noteDraftRef.current = noteDraft;
  const saveNote = useCallback(
    async (text: string): Promise<boolean> => {
      if (!report) return true;
      setSaveState('saving');
      try {
        await actions.setNote.mutateAsync({ reportId: report.id, note: text });
        setNoteDraft((d) => (d === text ? null : d));
        setSaveState('saved');
        return true;
      } catch (e) {
        setSaveState(navigator.onLine ? 'error' : 'offline');
        setError(errorText(e));
        return false;
      }
    },
    [report, actions.setNote],
  );
  const setNote = useCallback(
    (text: string) => {
      setNoteDraft(text);
      if (noteTimer.current) clearTimeout(noteTimer.current);
      noteTimer.current = setTimeout(() => {
        noteTimer.current = null;
        void saveNote(text);
      }, 700);
    },
    [saveNote],
  );
  /** 补充说明还有没保存的改动时立即保存 */
  const flushNote = useCallback(async () => {
    if (!noteTimer.current) return true;
    clearTimeout(noteTimer.current);
    noteTimer.current = null;
    return noteDraftRef.current === null ? true : saveNote(noteDraftRef.current);
  }, [saveNote]);

  // ---------- 提交 ----------
  /** 出差当天：写一句话就行（进展或补充说明都可以），不追问 */
  const isTrip = report?.leave?.type === 'trip';
  const itemsFilled = !!report?.items.some((i) => textOf(i).trim() || i.noProgress);
  /** 能直接提交：写了进展；出差或名下没有项目时，只写补充说明也行 */
  const canSubmitNow = itemsFilled || ((isTrip || report?.items.length === 0) && !!note.trim());
  /** 按钮可点：写了任何东西都可以点，缺进展时点了会说明原因 */
  const hasContent = canSubmitNow || !!note.trim();
  useEffect(() => {
    if (isTrip && editable) setNotice((n) => n ?? '今天出差：写一句话就行，写在进展或补充说明里都可以，不会追问');
  }, [isTrip, editable]);

  const submit = useCallback(async () => {
    if (!report) return;
    setError(null);
    if (!canSubmitNow) {
      setError('补充说明之外，至少给一个项目写一句进展；今天没动的项目可以标「今天没有进展」');
      return;
    }
    // 回复框里还没发出去的回复，先发出去，不能随提交丢掉
    if (replyTarget && reply.trim() && !(await answer(replyTarget, reply))) return;
    const saved = (await flush()) && (await flushNote());
    if (!saved) {
      setError((e) => e ?? '有内容没保存成功，改好后再提交');
      return;
    }
    try {
      const r = await actions.submit.mutateAsync(report.id);
      if (r.status === 'follow_up') setNotice('AI 有一个问题，回答一下；不确定也可以直接再按一次提交');
      else setNotice(null);
    } catch (e) {
      setError(errorText(e));
    }
  }, [report, canSubmitNow, replyTarget, reply, answer, flush, flushNote, actions.submit]);

  const retract = useCallback(async () => {
    if (!report) return;
    try {
      await actions.retract.mutateAsync(report.id);
      setNotice('已撤回，改完记得再提交。主管看到的仍是上一个提交版本');
    } catch (e) {
      setError(errorText(e));
    }
  }, [report, actions.retract]);

  /** 回复框发送：有追问时回复追问 */
  const sendReply = useCallback(async () => {
    if (replyTarget && reply.trim()) return answer(replyTarget, reply);
  }, [replyTarget, reply, answer]);

  // ---------- 截止时间 ----------
  const now = serverNow();
  const deadlineAt = report ? cnInstant(report.date, report.deadline) : null;
  const minutesLeft = deadlineAt ? Math.round((deadlineAt.getTime() - now.getTime()) / 60_000) : null;

  return {
    actions,
    editable,
    drafts,
    textOf,
    setItemText,
    flush,
    revert,
    saveState,
    error,
    setError,
    notice,
    setNotice,
    reply,
    setReply,
    replyTarget,
    pendingManager,
    pendingAi,
    answer,
    skip,
    submit,
    retract,
    sendReply,
    note,
    setNote,
    hasContent,
    minutesLeft,
    editedCount: report?.items.filter((i) => i.origin === 'edited').length ?? 0,
  };
}

export type ReportEditor = ReturnType<typeof useReportEditor>;

/** 行右侧的说明 */
export function itemMeta(item: ReportItemDTO, text: string, followUp: FollowUpDTO | undefined): { label: string; tone: 3 | 4 } | null {
  if (followUp && !followUp.answer && !followUp.skipped) return { label: '1 条追问', tone: 3 };
  if (item.noProgress) return { label: '今天没有进展', tone: 4 };
  if (!text.trim()) return { label: '未填写', tone: 4 };
  if (item.origin === 'edited' || (item.prefilledText != null && text !== item.prefilledText)) return { label: '已修改', tone: 4 };
  if (item.origin === 'ai') return { label: '预填', tone: 4 };
  return null;
}

export function durationText(minutes: number): string {
  const m = Math.abs(minutes);
  if (m < 60) return `${m} 分钟`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest >= 10 ? `${h} 小时 ${rest} 分钟` : `${h} 小时`;
}
